import React, { useCallback } from 'react'
import { FormControl, MenuItem, Select, SelectChangeEvent, useTheme } from '@mui/material'
import { useTranslation } from 'react-i18next'
import {
  DEFAULT_BODY_STYLES,
  DEFAULT_HEADER_STYLES,
  ITextComponentInstance,
  ITextStyles,
  TextAlignment,
  TextType,
} from 'src/types/report-designer'
import { AppTextField } from '../../AppTextField'
import {
  AlignmentToggleGroup,
  BoldToggleRow,
  ColorSwatchField,
  FontSizeSelect,
  FontFamilySelect,
  LineHeightSelect,
  LetterSpacingSelect,
} from './PropertyComponents'
import {
  FieldSection,
  getTextFontColors,
  PropertiesWrapper,
  PropertyLabel,
  SectionHeading,
  StyledDivider,
} from './PropertiesShared.styles'

export interface ITextComponentPropertiesProps {
  instance: ITextComponentInstance
  onChange: (updated: ITextComponentInstance) => void
}

export const TextComponentProperties: React.FC<ITextComponentPropertiesProps> = ({ instance, onChange }) => {
  const theme = useTheme()
  const { t: translate } = useTranslation(['dynamic-report'])
  const FONT_COLORS = getTextFontColors(theme)

  const handleContentChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange({ ...instance, content: e.target.value })
    },
    [instance, onChange],
  )

  const handleTextTypeChange = useCallback(
    (e: SelectChangeEvent<number>) => {
      const newType = Number(e.target.value) as TextType
      const defaultStyles = newType === TextType.Header ? DEFAULT_HEADER_STYLES(theme) : DEFAULT_BODY_STYLES(theme)
      onChange({ ...instance, textType: newType, styles: defaultStyles })
    },
    [instance, onChange],
  )

  const handleStyleChange = useCallback(
    <K extends keyof ITextStyles>(key: K, value: ITextStyles[K]) => {
      onChange({ ...instance, styles: { ...instance.styles, [key]: value } })
    },
    [instance, onChange],
  )

  const handleAlignmentChange = useCallback(
    (_: React.MouseEvent<HTMLElement>, value: TextAlignment | null) => {
      if (value) handleStyleChange('alignment', value)
    },
    [handleStyleChange],
  )

  return (
    <PropertiesWrapper>
      <FieldSection>
        <PropertyLabel>{translate('text-content', { ns: 'dynamic-report' })}</PropertyLabel>
        <AppTextField
          size="small"
          fullWidth
          multiline
          minRows={3}
          value={instance.content}
          onChange={handleContentChange}
          placeholder={translate('text-content-placeholder', { ns: 'dynamic-report' })}
        />
      </FieldSection>

      <FieldSection>
        <PropertyLabel>{translate('text-type', { ns: 'dynamic-report' })}</PropertyLabel>
        <FormControl size="small" fullWidth>
          <Select<number> value={instance.textType} onChange={handleTextTypeChange}>
            <MenuItem value={TextType.Header}>{translate('text-type-header', { ns: 'dynamic-report' })}</MenuItem>
            <MenuItem value={TextType.Body}>{translate('text-type-body', { ns: 'dynamic-report' })}</MenuItem>
          </Select>
        </FormControl>
      </FieldSection>

      <StyledDivider />

      <SectionHeading>{translate('styles', { ns: 'dynamic-report' })}</SectionHeading>

      <FieldSection>
        <PropertyLabel>{translate('font-color', { ns: 'dynamic-report' })}</PropertyLabel>
        <ColorSwatchField
          colors={FONT_COLORS}
          selectedColor={instance.styles.fontColor}
          onColorChange={color => handleStyleChange('fontColor', color)}
        />
      </FieldSection>

      <FieldSection>
        <PropertyLabel>{translate('font-size', { ns: 'dynamic-report' })}</PropertyLabel>
        <FontSizeSelect value={instance.styles.fontSize} onChange={size => handleStyleChange('fontSize', size)} />
      </FieldSection>

      <FieldSection>
        <PropertyLabel>{translate('font-family', { ns: 'dynamic-report' })}</PropertyLabel>
        <FontFamilySelect
          value={instance.styles.fontFamily}
          onChange={family => handleStyleChange('fontFamily', family)}
        />
      </FieldSection>

      <FieldSection>
        <PropertyLabel>{translate('alignment', { ns: 'dynamic-report' })}</PropertyLabel>
        <AlignmentToggleGroup value={instance.styles.alignment} onChange={handleAlignmentChange} />
      </FieldSection>

      <BoldToggleRow
        label={translate('bold', { ns: 'dynamic-report' })}
        checked={instance.styles.bold}
        onChange={checked => handleStyleChange('bold', checked)}
      />

      <FieldSection>
        <PropertyLabel>{translate('line-height', { ns: 'dynamic-report' })}</PropertyLabel>
        <LineHeightSelect
          value={instance.styles.lineHeight ?? 1.4}
          onChange={val => handleStyleChange('lineHeight', val)}
        />
      </FieldSection>

      <FieldSection>
        <PropertyLabel>{translate('letter-spacing', { ns: 'dynamic-report' })}</PropertyLabel>
        <LetterSpacingSelect
          value={instance.styles.letterSpacing ?? 0}
          onChange={val => handleStyleChange('letterSpacing', val)}
        />
      </FieldSection>
    </PropertiesWrapper>
  )
}
